import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const r = await register(name, email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Studio unlocked");
      nav("/dashboard", { replace: true });
    } else {
      setError(r.error);
    }
  }

  return (
    <div className="min-h-screen bg-[#C7C9CC] text-[#0A0A0A] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-gradient-to-br from-[#BFC2C6] via-[#C7C9CC] to-[#B0B3B7]">
        <Link to="/" className="font-display text-2xl tracking-tighter">BEAT BOX</Link>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="bb-massive text-white/40 text-[24vw]">JOIN<br />IN</span>
        </div>
        <div className="relative z-10">
          <span className="bb-tag">— FIRST TIME?</span>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 tracking-tighter leading-[0.9]">
            Build your<br />sound profile.
          </h1>
          <p className="text-[#2B2B2B] mt-5 max-w-md">Free while in beta. Multi-track, Drumify, Sound Guy assistant, WAV + MP3 export — all unlocked.</p>
        </div>
        <p className="font-mono text-[#6E7378] text-xs uppercase tracking-widest relative z-10">CTRL ROOM · 002</p>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-[#E2E3E5]">
        <form onSubmit={handleSubmit} className="w-full max-w-md bb-card p-8 lg:p-10" data-testid="register-form">
          <Link to="/" className="font-display text-xl tracking-tighter lg:hidden block mb-6">BEAT BOX</Link>
          <span className="bb-tag">SIGN UP</span>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter mt-4 mb-8 leading-[0.95]">
            Create<br />account.
          </h2>
          <div className="space-y-5">
            <div>
              <Label className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">Artist name</Label>
              <Input
                required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">Email</Label>
              <Input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">Password</Label>
              <Input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5"
                data-testid="register-password-input"
              />
              <p className="text-[#6E7378] text-xs mt-1 font-mono">Min 6 characters</p>
            </div>
            {error && <div className="text-[#E11D48] text-sm font-mono" data-testid="register-error">{error}</div>}
            <Button
              type="submit" disabled={loading}
              data-testid="register-submit-btn"
              className="bb-pill w-full bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-12 font-semibold"
            >
              {loading ? "Creating…" : "Get the studio"} <ArrowRight className="ml-2" size={16} weight="bold" />
            </Button>
            <GoogleSignInButton label="Sign up with Google" />
          </div>
          <p className="text-[#6E7378] text-sm mt-6 text-center">
            Already have one?{" "}
            <Link to="/login" data-testid="link-login" className="text-[#0A0A0A] font-semibold underline underline-offset-4 decoration-[#A8FF1F] decoration-[3px]">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
